import type { StoreTaxSettings } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { SalesTaxSectionActions, SalesTaxSectionState } from "./sales-tax-section-model";

type Note = {
  text: string;
  tone: "danger" | "warn" | "accent";
  /** Whether this is fixed in the merchant's Stripe dashboard rather than here. */
  inStripe?: boolean;
};

const noteClass: Record<Note["tone"], string> = {
  danger: "note-danger text-danger",
  warn: "note-warn text-warn",
  accent: "note-accent text-accent",
};

/**
 * What this setting is actually doing.
 *
 * Every shop collects by default, so this panel almost never reports a choice —
 * it reports why the default is or is not producing tax. The warnings are not
 * gated on `collectSalesTax` for that reason: they are the reasons a shop is
 * charging nothing, and they read the same whether or not the merchant has
 * since switched collection off.
 *
 * The order matters — each answer is only meaningful once the one above it is
 * settled: payments being refused outright, then no account, then Tax not
 * switched on, then registered nowhere, then registered somewhere that is not
 * here.
 */
function readiness(settings: StoreTaxSettings): Note | null {
  // The one state that costs sales rather than tax. `quoteForCart` throws when
  // it must compute tax and has no address to source it to, which refuses the
  // checkout — so this is said as the outage it is, and names the two ways out
  // in the order a merchant with a customer waiting wants them.
  if (settings.collectSalesTax && settings.taxStatus === "active" && !settings.hasSourceAddress) {
    return {
      text: "Card payments are being refused. Stripe Tax is active on your account and sales tax is on, but this shop's address is too incomplete to work the tax out from. Add a complete address to your location — or turn sales tax off below to keep selling while you do.",
      tone: "danger",
    };
  }
  if (!settings.accountConnected || !settings.hasSourceAddress) return null;

  if (settings.taxStatus !== "active") {
    return {
      text: "Sales tax is on here, but Stripe Tax isn't switched on for your account yet — so nothing is being added. Turn it on in your Stripe dashboard under Settings → Tax.",
      tone: "warn",
      inStripe: true,
    };
  }
  if (settings.registrations.length === 0) {
    return {
      text: "Your Stripe account has no tax registrations, so nothing is being added. Add the places you're registered to collect in and tax starts applying on its own.",
      tone: "warn",
      inStripe: true,
    };
  }
  // Registered, but somewhere else. The case a registration *count* hides, and
  // the one that reads as a broken switch: the merchant turns it on, prices
  // don't move, and nothing says why.
  if (!settings.sourceCovered) {
    return {
      text: `You're registered in ${settings.registrations.join(", ")}, but this shop sells from ${settings.sourceJurisdiction}. Stripe only charges tax where you're registered, so these sales stay untaxed until you add a registration for ${settings.sourceJurisdiction}.`,
      tone: "warn",
      inStripe: true,
    };
  }
  if (settings.collectSalesTax) {
    return {
      text: `Tax is being added to card sales, worked out from ${settings.sourceJurisdiction}.`,
      tone: "accent",
    };
  }
  // Registered here and charging nothing. Collection is on for every shop, so
  // this can only be the merchant's own override — said as that, rather than as
  // a step they forgot, and fixed by this panel's own button rather than Stripe.
  return {
    text: `You've turned this off. Your Stripe account is registered in ${settings.sourceJurisdiction}, where this shop sells from, so these sales are untaxed until you turn it back on.`,
    tone: "warn",
  };
}

/** One "label — value" row of the comparison the warnings are drawn from. */
function Fact({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-xs text-faint">{label}</span>
      <span className={`text-right text-[13px] ${muted ? "text-muted" : "text-white"}`}>{value}</span>
    </div>
  );
}

function taxCodeLabel(id: string | null, name: string | null): string | null {
  if (!id) return null;
  return name ? `${name} (${id})` : id;
}

function taxStatusLabel(status: string | null): string {
  if (status === "active") return "Active";
  if (status === "pending") return "Pending — Stripe is still missing something, usually the head office address";
  if (status === null) return "Not enabled, or never read";
  return status;
}

const checkedAt = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * Sales tax on card sales.
 *
 * Below Providers on purpose: it has no meaning without a connected Stripe
 * account, and the registrations that decide what it charges live in the
 * merchant's own Stripe dashboard rather than here.
 *
 * Collection is on for every shop, so this panel reports a state rather than
 * offering a choice — there is no switch to forget, which is the whole point.
 * The button is the way out, not the way in, and it stays usable in exactly the
 * state where it matters most.
 *
 * The panel shows the two facts it compares — where the shop sells from, and
 * where the account is registered — rather than only the verdict. A merchant
 * told "1 registration" cannot see that it is the wrong one; "US-IL" sitting
 * next to "UA" needs no explaining.
 */
export function SalesTaxSection() {
  const state = useSubtree(SalesTaxSectionState);
  const actions = useSubtree(SalesTaxSectionActions);
  const observer = useObserver();
  const settings = observer.watch(state.settings);
  const status = observer.watch(state.status);
  const fetching = observer.watch(state.fetching);
  const checked = observer.watch(state.checkedAt);
  const form = observer.watch(state.form);

  if (status.kind === "idle" || status.kind === "loading") {
    return <div className="h-40 animate-pulse rounded-card bg-surface-strong" />;
  }
  if (!settings) return null;

  const note = readiness(settings);
  // Only turning it *on* has prerequisites; the server refuses that without an
  // address and never refuses an off. Disabling the button outright would trap
  // the merchant the danger note is talking to — payments refused, escape hatch
  // greyed out — so the guard applies to one direction only.
  const cannotTurnOn = !settings.accountConnected || !settings.hasSourceAddress;
  const disabled = (!settings.collectSalesTax && cannotTurnOn) || form.busy;
  // "On" and "charging" stopped being the same thing when every shop became on.
  // Saying "Charging sales tax" over a shop Stripe quotes zero for is the exact
  // confusion the rest of this panel exists to undo.
  const charging = settings.collectSalesTax && settings.taxStatus === "active" && settings.sourceCovered;

  return (
    <section className="panel p-6 sm:p-8">
      <div className="mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-white">Sales tax</h2>
          {/* Every read re-asks Stripe and rewrites the cache the checkout
              prices by, so this is the "is it in sync yet" button. */}
          {settings.accountConnected && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-faint">
                {fetching ? "Checking Stripe…" : `Checked at ${checkedAt(checked ?? Date.now())}`}
              </span>
              <button
                type="button"
                onClick={actions.refresh}
                disabled={fetching}
                className="btn-secondary btn-sm shrink-0"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
        <p className="mt-1 text-sm text-muted">
          Add sales tax to the cards customers buy from you. It's worked out from your shop's address — the sale happens
          at your counter — and it's your responsibility to register and file wherever you owe it. It's on for every
          shop, and Stripe adds tax only where your account is registered — so registering there is all it takes, and
          there's nothing to switch on here.
        </p>
      </div>

      {form.error && <p className="note note-danger mb-4 text-danger">{form.error}</p>}

      {settings.stripeReadError && (
        <p className="note note-danger mb-4 text-danger">
          Stripe couldn't be read just now, so the values below are the last ones saved — which is also what a checkout
          charges by until Stripe answers again. Stripe said: {settings.stripeReadError}
        </p>
      )}

      {!settings.accountConnected && (
        <p className="note note-warn mb-4 text-warn">
          Connect Stripe first — sales tax is worked out by Stripe Tax on your connected account.
        </p>
      )}
      {/* Silent when the note below is already saying it louder: with Stripe Tax
          active a missing address refuses payments, and one panel should not
          state the same fault twice at two different volumes. */}
      {settings.accountConnected && !settings.hasSourceAddress && note?.tone !== "danger" && (
        <p className="note note-warn mb-4 text-warn">
          Add a complete address to one of your locations — the tax is worked out from where your shop is.
        </p>
      )}

      {/* The comparison itself. Shown even while the setting is off, because
          this is what a merchant needs in order to decide. */}
      {settings.accountConnected && (
        <div className="mb-4 divide-y divide-line rounded-row bg-surface px-4 py-1">
          <Fact label="Stripe account" value={`${settings.accountId ?? "—"} (${settings.stripeMode} mode)`} />
          <Fact label="Stripe Tax" value={taxStatusLabel(settings.taxStatus)} muted={settings.taxStatus !== "active"} />
          <Fact
            label="Taxed from"
            value={
              settings.sourceJurisdiction
                ? settings.sourceLocationName
                  ? `${settings.sourceLocationName} — ${settings.sourceJurisdiction}`
                  : settings.sourceJurisdiction
                : "No usable address"
            }
            muted={!settings.sourceJurisdiction}
          />
          <Fact
            label="Registered in"
            value={settings.registrations.length ? settings.registrations.join(", ") : "Nowhere yet"}
            muted={settings.registrations.length === 0}
          />
          <Fact
            label="Registered where this shop sells"
            value={settings.sourceCovered ? "Yes" : "No"}
            muted={!settings.sourceCovered}
          />
          <Fact
            label="Collection here"
            value={settings.collectSalesTax ? "On" : "Off"}
            muted={!settings.collectSalesTax}
          />
          <Fact
            label="Default product tax code in Stripe"
            value={taxCodeLabel(settings.defaultTaxCodeId, settings.defaultTaxCodeName) ?? "Not set"}
            muted={!settings.defaultTaxCodeId}
          />
          <Fact label="Cards are taxed as" value={`${settings.cardTaxCodeName} (${settings.cardTaxCodeId})`} />
        </div>
      )}

      {/* The default only matters for what it hides: a merchant who picked
          "Services" in Stripe's setup and expects it to apply. It never does. */}
      {settings.accountConnected &&
        settings.defaultTaxCodeId &&
        settings.defaultTaxCodeId !== settings.cardTaxCodeId && (
          <p className="note note-warn mb-4 text-warn">
            Your Stripe account's default product tax code is{" "}
            {taxCodeLabel(settings.defaultTaxCodeId, settings.defaultTaxCodeName)}. That default is not used for card
            sales — every card is sent to Stripe Tax as {settings.cardTaxCodeName}, which is what a trading card is. You
            can change the default in Stripe to match, but nothing here depends on it.
          </p>
        )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-medium text-white">
            {charging
              ? "Charging sales tax"
              : settings.collectSalesTax
                ? "Nothing is being added yet"
                : "Sales tax is off"}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-faint">
            Applies to card sales through a customer's cart, in person and online.
          </p>
        </div>
        <button
          type="button"
          onClick={actions.toggle}
          disabled={disabled}
          className={settings.collectSalesTax ? "btn-secondary btn-sm shrink-0" : "btn-primary btn-sm shrink-0"}
        >
          {form.busy ? "Saving…" : settings.collectSalesTax ? "Turn off" : "Turn on"}
        </button>
      </div>

      {note && <p className={`note mt-4 ${noteClass[note.tone]}`}>{note.text}</p>}

      {/* The warnings that are fixed in Stripe say where. The one that isn't —
          a merchant who turned collection off — is fixed by the button above. */}
      {settings.accountConnected && note?.inStripe && (
        <p className="mt-3 text-xs text-faint">
          <a href={settings.registrationsUrl} target="_blank" rel="noreferrer noopener" className="underline">
            Open tax registrations in Stripe
          </a>
        </p>
      )}

      {/* The single-source limitation, named only for the shops it affects: a
          branch in another jurisdiction is still taxed from the one above. */}
      {settings.otherLocationJurisdictions.length > 0 && (
        <p className="note note-warn mt-3 text-warn">
          You also have {settings.otherLocationJurisdictions.length === 1 ? "a branch" : "branches"} in{" "}
          {settings.otherLocationJurisdictions.join(", ")}. Every sale is taxed from {settings.sourceJurisdiction}{" "}
          regardless of which branch it was made at — we can't yet tell which counter a customer is standing at.
        </p>
      )}
    </section>
  );
}
